import React, { useEffect, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { adminAuth } from "@/lib/api";
import { DollarSign, AlertCircle, Loader2, ExternalLink, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/university`;
const auth = () => ({ Authorization: `Bearer ${adminAuth.getToken()}` });

export default function AdminUniversityBilling() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");  // all | pending_payment | active | past_due

  const load = async () => {
    setLoading(true);
    try {
      const s = await axios.get(`${API}/admin/billing/summary`, { headers: auth() });
      setSummary(s.data);
      const params = filter === "past_due" ? { past_due_only: true }
        : filter === "all" ? {} : { status: filter };
      const list = await axios.get(`${API}/admin/billing/students`, { headers: auth(), params });
      setStudents(list.data?.items || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login?next=/admin/university/billing");
      else toast.error("Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <AdminShell door="university" title="Billing Dashboard">
      <div data-testid="admin-billing-page">
        {loading || !summary ? <Loader2 className="w-4 h-4 animate-spin text-slate-500" /> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              <Tile icon={DollarSign} label="Revenue (paid)" value={`$${summary.revenue_paid_usd.toLocaleString()}`} testid="bill-revenue" />
              <Tile icon={DollarSign} label="Outstanding (pending)" value={`$${summary.pending_payment_outstanding_usd.toLocaleString()}`} testid="bill-outstanding" />
              <Tile icon={DollarSign} label="Partial balances" value={`$${summary.outstanding_paid_partial_usd.toLocaleString()}`} testid="bill-partial" />
              <Tile icon={AlertCircle} label="Past due (>7d)" value={summary.past_due_count} testid="bill-pastdue" />
              <Tile icon={DollarSign} label="Active/Grad enrollments" value={summary.active_or_grad_count} testid="bill-active" />
            </div>

            <div className="flex items-center gap-2 mb-4">
              {[["all", "All"], ["pending_payment", "Pending"], ["active", "Active"], ["graduated", "Graduated"], ["past_due", "Past Due"]].map(([k, l]) => (
                <button key={k} onClick={() => setFilter(k)} data-testid={`bill-filter-${k}`}
                  className={`rounded-full h-8 px-3 text-xs ${filter === k ? "bg-purple-900 text-white" : "bg-white border border-slate-200 text-slate-700"}`}>{l}</button>
              ))}
            </div>

            <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr><th className="text-left px-4 py-2">Student</th><th className="text-left px-4 py-2">Program</th>
                  <th className="text-left px-4 py-2">Status</th><th className="text-right px-4 py-2">Total</th>
                  <th className="text-right px-4 py-2">Paid</th><th className="text-right px-4 py-2">Balance</th>
                  <th className="text-left px-4 py-2">Created</th><th className="text-right px-4 py-2">Actions</th></tr>
                </thead>
                <tbody>
                  {students.length === 0 && <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400">No enrollments match.</td></tr>}
                  {students.map((s) => (
                    <tr key={s.id} className="border-t border-slate-100" data-testid={`bill-row-${s.id}`}>
                      <td className="px-4 py-2"><div className="font-medium">{s.student_name}</div><div className="text-xs text-slate-500">{s.student_email}</div></td>
                      <td className="px-4 py-2 text-xs">{s.program_key}</td>
                      <td className="px-4 py-2"><span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${s.status === "active" ? "bg-emerald-100 text-emerald-700" : s.status === "pending_payment" ? "bg-amber-100 text-amber-800" : s.status === "graduated" ? "bg-purple-100 text-purple-900" : "bg-slate-100 text-slate-600"}`}>{s.status}</span></td>
                      <td className="px-4 py-2 text-right tabular-nums">${(s.tuition_total_usd || 0).toLocaleString()}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-emerald-700">${(s.tuition_paid_usd || 0).toLocaleString()}</td>
                      <td className={`px-4 py-2 text-right tabular-nums ${(s.tuition_balance_usd || 0) > 0 ? "text-rose-700 font-medium" : "text-slate-400"}`}>${(s.tuition_balance_usd || 0).toLocaleString()}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">{(s.created_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-2 text-right">
                        {s.square_payment_url && (
                          <a href={s.square_payment_url} target="_blank" rel="noopener noreferrer" className="text-purple-900 mr-3 inline-flex items-center gap-0.5" title="Payment link"><ExternalLink className="w-3.5 h-3.5" /></a>
                        )}
                        <a href={`mailto:${s.student_email}`} className="text-slate-500" title="Email"><Mail className="w-3.5 h-3.5" /></a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </AdminShell>
  );
}

function Tile({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500"><Icon className="w-3.5 h-3.5" />{label}</div>
      <div className="text-2xl font-display text-slate-900 mt-1">{value}</div>
    </div>
  );
}
